import {
  getBillingFrequencyFromNumber,
  getServiceDurationFromNumber,
  formatBillingFrequency,
  formatServiceDuration
} from "@/pages/settings/pages/products-v2/utils";
import {
  ServiceDuration,
  BillingFrequency
} from "@/pages/settings/pages/products-v2/types";
import {
  ContractRevenueItem,
  EqPaymentsBillingFrequency
} from "@/pages/contracts/types";
import { TFunction } from "i18next";
import { addDays, addMonths, differenceInMonths, subDays } from "date-fns";
import { toLocalDate, toDateOnlyISO } from "@/utils/datetime";
import { RevenueItemStatus } from "./types";

export const revenueItemStatusColors: Record<RevenueItemStatus, string> = {
  [RevenueItemStatus.ACTIVE]: "var(--charts-positive)",
  [RevenueItemStatus.UPCOMING]: "var(--natural)",
  [RevenueItemStatus.EXPIRED]: "var(--charts-negative)",
  [RevenueItemStatus.ONE_TIME]: "var(--new)",
  [RevenueItemStatus.DRAFT]: "var(--border-field-default)"
};

export const getBillingFrequencyLabel = (
  frequency: number | null | undefined,
  t: TFunction
): string => {
  const frequencyEnum = getBillingFrequencyFromNumber(frequency ?? null);
  if (!frequencyEnum) {
    return t("settings.product.billingFrequency.annually");
  }
  return formatBillingFrequency(frequencyEnum, t);
};

export const getBillingPeriodLabel = (
  frequency: number | null | undefined,
  t: TFunction
): string => {
  const frequencyEnum = getBillingFrequencyFromNumber(frequency ?? null);
  if (!frequencyEnum) {
    return t("settings.product.billingFrequencyShort.annually");
  }
  return formatBillingFrequency(frequencyEnum, t, true);
};

export const getServiceDurationLabel = (
  duration: number | null | undefined,
  t: TFunction
): string => {
  const durationEnum = getServiceDurationFromNumber(duration ?? null);
  if (!durationEnum) {
    return "—";
  }
  return formatServiceDuration(durationEnum, t);
};

export const getServiceDurationInMonths = (
  duration: number | null | undefined
): number => {
  switch (duration) {
    case ServiceDuration.ONE_MONTH:
      return 1;
    case ServiceDuration.ONE_YEAR:
      return 12;
    case ServiceDuration.TWO_YEARS:
      return 24;
    case ServiceDuration.THREE_YEARS:
      return 36;
    default:
      return 1;
  }
};

export const getBillingFrequencyInMonths = (
  frequency: number | null | undefined
): number => {
  switch (frequency) {
    case BillingFrequency.MONTHLY:
      return 1;
    case BillingFrequency.QUARTERLY:
      return 3;
    case BillingFrequency.ANNUALLY:
      return 12;
    case BillingFrequency.ONCE:
      return 0;
    default:
      return 1;
  }
};

// The one place the equal-payments period enum is spelled out as copy. Total by
// construction, so a new member is a compile error rather than a silent label.
export const EQ_PERIOD_LABEL_KEYS: Record<EqPaymentsBillingFrequency, string> =
  {
    [EqPaymentsBillingFrequency.DAYS]:
      "contractTerms.billingFrequencyLabels.days",
    [EqPaymentsBillingFrequency.WEEKS]:
      "contractTerms.billingFrequencyLabels.weeks",
    [EqPaymentsBillingFrequency.MONTHS]:
      "contractTerms.billingFrequencyLabels.months",
    [EqPaymentsBillingFrequency.YEARS]:
      "contractTerms.billingFrequencyLabels.years"
  };

export const getEqPeriodInMonths = (
  period: EqPaymentsBillingFrequency
): number => {
  switch (period) {
    case EqPaymentsBillingFrequency.DAYS:
      return 1 / 30;
    case EqPaymentsBillingFrequency.WEEKS:
      return 1 / 4;
    case EqPaymentsBillingFrequency.MONTHS:
      return 1;
    case EqPaymentsBillingFrequency.YEARS:
      return 12;
    default:
      return 1;
  }
};

export const computeRecurringPaymentCount = (
  serviceDurationInMonths: number,
  billingEvery: number,
  period: EqPaymentsBillingFrequency
): number => {
  const frequencyInMonths = billingEvery * getEqPeriodInMonths(period);
  if (frequencyInMonths <= 0) return 1;
  return Math.max(1, Math.ceil(serviceDurationInMonths / frequencyInMonths));
};

export const getNumberOfPayments = (
  serviceDuration: number | null | undefined,
  billingFrequency: number | null | undefined
): number => {
  const durationInMonths = getServiceDurationInMonths(serviceDuration);
  const frequencyInMonths = getBillingFrequencyInMonths(billingFrequency);

  if (frequencyInMonths === 0) return 1; // One-time payment
  return Math.ceil(durationInMonths / frequencyInMonths);
};

// API returns these as UTC-midnight timestamps; toLocalDate keeps the calendar day west of UTC.
export const formatBillingStartDate = (dateString: string): string => {
  if (!dateString) return "";
  const date = toLocalDate(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric"
  };
  return date.toLocaleDateString("en-US", options);
};

export const formatDateShort = (dateString: string): string => {
  if (!dateString) return "";
  const date = toLocalDate(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric"
  };
  return date.toLocaleDateString("en-GB", options).replace(",", "");
};

export const formatDateRange = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return "";
  return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`;
};

/**
 * Last day of a term running `months` whole months from `start`.
 *
 * Normally the day before the anniversary: 1 Jul + 12 months ends 30 Jun.
 * When the anniversary day doesn't exist in the target month, addMonths
 * clamps it (31 Jan + 1 month gives 28 Feb) and that clamped day *is* the
 * end of the term — there is no 31 Feb for it to fall short of. So 31 Jan
 * + 1 month ends 28 Feb, while 28 Jan + 1 month ends 27 Feb.
 *
 * Expects `months` >= 1; the only callers derive it from
 * getServiceDurationInMonths, which returns 1, 12, 24 or 36.
 */
export const getMonthTermEndDate = (start: Date, months: number): Date => {
  const anniversary = addMonths(start, months);
  return anniversary.getDate() === start.getDate()
    ? subDays(anniversary, 1)
    : anniversary;
};

export const calculateBillingEndDate = (
  billingStartDate: string,
  serviceDuration: number | null | undefined
): string => {
  if (!billingStartDate) return "";
  const startDate = toLocalDate(billingStartDate);
  // Without this, an unparseable input formats as the string "NaN-NaN-NaN"
  // rather than failing — matching the guard calculateServiceEndDate already has.
  if (isNaN(startDate.getTime())) return "";
  const durationInMonths = getServiceDurationInMonths(serviceDuration);
  return toDateOnlyISO(getMonthTermEndDate(startDate, durationInMonths));
};

// Defaults to cents only when the amount has them, so line items stop rounding away from ARR.
export const formatCurrency = (
  amount: number | null | undefined,
  currency?: string | null,
  decimals?: number,
  // Replaces only the sign, never the figure — the fraction digits stay the
  // currency's own. Callers that render this figure beside one in the
  // account's currency pass a sign already resolved against it, so the two
  // halves cannot both print the same glyph.
  signOverride?: string
): string => {
  if (amount == null) return "—";
  // `??` (not a default param) so a null currency — e.g. a contract with no
  // currency set — falls back too; a default param only fires on undefined.
  const resolvedCurrency = currency ?? "USD";
  // Cents-rounded only to decide the precision — float tails (1200.0000000000002)
  // must not read as fractional. Intl does the actual rounding, at whatever
  // precision the caller asked for.
  const centsRounded = Math.round(amount * 100) / 100;
  const fractionDigits = decimals ?? (Number.isInteger(centsRounded) ? 0 : 2);
  let parts: Intl.NumberFormatPart[];
  try {
    parts = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: resolvedCurrency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }).formatToParts(amount);
  } catch (error) {
    // Guarded for the same reason getCurrencySymbol below is, and this one
    // renders on far more surfaces: a currency Intl rejects would otherwise
    // throw through a render with no error boundary to stop it. Naming the
    // code is what the Intl-backed resolver in numbers.tsx does for this case.
    if (!(error instanceof RangeError)) throw error;
    // Decimal style takes no currency, so it cannot throw the same way.
    // Signed separately: the formatter puts the minus on the number, which
    // would leave the sign inside it ("USD-99.99") where the success path
    // renders "-USD99.99".
    const value = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }).format(Math.abs(amount));
    const minus = amount < 0 ? "-" : "";
    return `${minus}${signOverride || resolvedCurrency}${value}`;
  }
  return parts
    .map((part) =>
      part.type === "currency" && signOverride ? signOverride : part.value
    )
    .join("");
};

// A discount as the Discount column shows it, a line's or a bundle's: a
// percentage, or an amount in the record's currency; nothing without one.
export const formatDiscountCell = (
  discountAmount: number | null | undefined,
  isDiscountPercentage: boolean | null | undefined,
  currency: string,
  currencySignOverride?: string
): string | null =>
  (discountAmount ?? 0) > 0
    ? isDiscountPercentage
      ? `${discountAmount}%`
      : formatCurrency(
          discountAmount,
          currency,
          undefined,
          currencySignOverride
        )
    : null;

export const getCurrencySymbol = (currency?: string | null): string => {
  const resolvedCurrency = currency ?? "USD";
  try {
    return (
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: resolvedCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value ?? "$"
    );
  } catch (error) {
    // Intl raises RangeError on anything that is not a three-letter code, and
    // a record's currency arrives from the API. The two currency hooks call
    // this on every render now, including on pages that never format a figure,
    // and the app mounts no error boundary — so an unguarded throw here blanks
    // the page rather than mis-rendering one cell.
    if (!(error instanceof RangeError)) throw error;
    return resolvedCurrency;
  }
};

// Input padding that clears an adornment at inset 0.75rem — fixed padding lets "A$"/"CA$" overlap the value.
// 0.75em per char is a deliberate over-estimate: `ch` measures a digit and falls short of capitals like "CA".
export const getSymbolAdornmentPadding = (symbol: string): string =>
  `calc(0.75rem + ${symbol.length} * 0.75em + 0.25rem)`;

// Quantity is typed into a text input so a leading "-" or "." survives long
// enough to finish the number — the parse therefore has to reject the
// intermediate states rather than coerce them. Number() (not parseFloat) so
// trailing junk like "2abc" fails instead of silently becoming 2.
export const parseQuantityInput = (rawQuantity: string): number => {
  const trimmed = rawQuantity.trim();
  return trimmed === "" ? NaN : Number(trimmed);
};

// The value the input shows for a stored quantity; mid-typing states parse to
// NaN and have no numeric rendering.
export const formatQuantityInput = (quantity: number): string =>
  Number.isFinite(quantity) ? String(quantity) : "";

// Zero is the only rejected quantity: negative records a downsell, fractional a
// part-unit. The API accepts 0 (provider syncs have always sent it), so this is
// the only place it is refused.
export const isValidQuantity = (quantity: number): boolean =>
  Number.isFinite(quantity) && quantity !== 0;

export const getPricePerUnitSuffix = (
  billingFrequency: number | null | undefined,
  t: TFunction
): string => {
  if (billingFrequency === BillingFrequency.ONCE || !billingFrequency) {
    return t("contractTerms.perUnit");
  }
  const period = getBillingPeriodLabel(billingFrequency, t);
  return t("contractTerms.perUnitPeriod", { period });
};

// What a line's discount takes off its total: a percentage of the total, or
// the entered amount, which is always positive, applied with the line's sign
// so a discount shrinks a downsell's credit the way a percentage does.
export const discountOf = (
  total: number,
  discountAmount: number | null | undefined,
  isDiscountPercentage: boolean | null | undefined
): number => {
  const discount = discountAmount || 0;
  if (discount === 0) return 0;
  return isDiscountPercentage
    ? (total * discount) / 100
    : Math.sign(total) * discount;
};

// A line's total after its own discount.
export const totalAfterDiscount = (
  total: number,
  discountAmount: number | null | undefined,
  isDiscountPercentage: boolean | null | undefined
): number => total - discountOf(total, discountAmount, isDiscountPercentage);

// A recorded total is net of discount, so undoing it recovers the figure the
// line was priced from. An amount discount is applied with the sign of the
// line, so the inverse carries that sign or a downsell comes back wrong; a
// 100% discount leaves the original indeterminate.
export const totalBeforeDiscount = (
  total: number | null | undefined,
  discountAmount: number | null | undefined,
  isDiscountPercentage: boolean | null | undefined
): number | null => {
  if (total == null) return null;
  const discount = discountAmount || 0;
  if (isDiscountPercentage) {
    // At or past the whole total the original is indeterminate.
    if (discount >= 100) return null;
    return total / (1 - discount / 100);
  }
  // An amount discount that takes the line to nothing leaves the same gap. The
  // magnitude is the discount, but a credit and a charge of that size both land
  // on zero, so which side it came from is gone and the sign cannot be read
  // back off the total the way every other case reads it.
  if (total === 0 && discount > 0) return null;
  return total + Math.sign(total) * discount;
};

export const recordedTotalBeforeDiscount = (
  item: ContractRevenueItem
): number | null =>
  totalBeforeDiscount(
    item.totalLocal ?? item.total,
    item.discountAmount,
    item.isDiscountPercentage
  );

// The months a start/end pair spans, counting the end date itself.
// The API stamps these midnight UTC, so reading them as instants lands a day
// early behind UTC — and when the two ends fall either side of a daylight-saving
// change they shift by different amounts and the term loses a month. toLocalDate
// keeps whichever calendar day each was written as.
export const monthsBetween = (startDate: string, endDate: string): number => {
  const start = toLocalDate(startDate);
  const end = toLocalDate(endDate);
  // An unparseable end of a term would otherwise carry NaN into every total
  // derived from it; a single month is what an unstated length already means.
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 1;
  return Math.max(1, differenceInMonths(addDays(end, 1), start));
};

// How many months a line with these dates and duration runs for; recorded lines
// and bundle lines both resolve it here. A custom term states its length in its
// own dates rather than in the duration enum, which carries no month count for
// it — reading one back yields the one-month default and understates the period
// by however long the term really is.
export const periodInMonths = (
  isOneTime: boolean,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  duration: number | null | undefined
): number => {
  if (isOneTime) return 1;
  // The dates are what the term actually ran for. The duration enum carries no
  // length for a custom term, and a term whose end date was moved by hand keeps
  // whichever fixed value it started with — so where both dates are present
  // they, not the enum, say how long the line is.
  if (startDate && endDate) return monthsBetween(startDate, endDate);
  return getServiceDurationInMonths(duration);
};

// How many months a recorded line runs for, from its own dates and duration.
export const recordedPeriodInMonths = (
  item: ContractRevenueItem,
  catalogPeriods: number | null | undefined,
  isOneTime: boolean
): number =>
  periodInMonths(
    isOneTime,
    item.serviceStartDate,
    item.serviceEndDate,
    item.customServiceDuration ?? catalogPeriods
  );
